import { Adjustment, Invoice, LineItem, SubTotal, Total } from "../../../../../pojo/Pricing";
import { S25Util } from "../../../../../util/s25-util";

export function isReferenceSet(invoice: Invoice | undefined): boolean {
    return !!invoice?.billDefn?.referenceSet;
}
export function isDefaultSet(invoice: Invoice | undefined): boolean {
    return !!invoice?.billDefn?.defaultSet;
}

/**
 * The default and reference sets are per event, so combining related events brings back one of each
 * for every event in the fetch. The user is priced one event at a time, so the sets are folded into
 * the one belonging to the event on screen and shown as a single invoice.
 *
 * A merged invoice is for display only. It answers to the primary's id while carrying line items
 * billed on several, so it must never reach `InvoiceMove`, whose payloads restate an invoice's
 * whole occurrence list from the items it holds. Each row carries its own `billId` instead, which is
 * what a write routes by.
 */
export namespace SystemSets {
    /** The API names the default set after the event it prices, the only place that id is stated. */
    export const DEFAULT_SET_NAME = /^DEFAULT_PRICING_SET_(\d+)$/;

    /** The event a system set belongs to, by its name, the events it declares, or what it bills. */
    export function eventIdOf(invoice: Invoice | undefined): number | undefined {
        const named = DEFAULT_SET_NAME.exec(invoice?.billDefn?.billName ?? "");
        if (named) return S25Util.parseInt(named[1]);
        return invoice?.billDefn?.events?.[0] ?? invoice?.billing?.lineItems?.[0]?.eventId;
    }

    function mergeLineItems(invoices: Invoice[]): LineItem[] {
        return invoices.flatMap((invoice) =>
            (invoice.billing?.lineItems ?? []).map((lineItem) => ({ ...lineItem, billId: invoice.id })),
        );
    }

    function mergeAdjustments(invoices: Invoice[]): Adjustment[] {
        return invoices.flatMap((invoice) =>
            (invoice.billing?.adjustments ?? []).map((adjustment) => ({ ...adjustment, billId: invoice.id })),
        );
    }

    /**
     * One subtotal entry holding every set's. Each group is read by the org, occurrence or profile it
     * names rather than by position, and no two sets name the same one, so gathering them leaves
     * each figure to be found where it was.
     */
    function mergeSubtotals(invoices: Invoice[]): SubTotal[] {
        const gather = <Entry>(group: (subtotal: SubTotal) => Entry[] | undefined): Entry[] =>
            invoices.flatMap((invoice) =>
                (invoice.billing?.subtotals ?? []).flatMap((subtotal) => group(subtotal) ?? []),
            );

        return [
            {
                account: gather((subtotal) => subtotal.account),
                accountEvent: gather((subtotal) => subtotal.accountEvent),
                accountOccurrence: gather((subtotal) => subtotal.accountOccurrence),
                accountProfile: gather((subtotal) => subtotal.accountProfile),
                profile: gather((subtotal) => subtotal.profile),
                occurrence: gather((subtotal) => subtotal.occurrence),
            },
        ];
    }

    function sumTotals(invoices: Invoice[]): Total {
        const summed: Record<string, number> = {};

        for (const invoice of invoices) {
            for (const [figure, amount] of Object.entries(invoice.billing?.totals ?? {})) {
                summed[figure] = (summed[figure] ?? 0) + (amount ?? 0);
            }
        }
        return summed as Total;
    }

    /**
     * `others` folded into `primary`, which keeps its id and its `billDefn` - renaming, locking and
     * the invoice number all stay the priced event's to change.
     */
    export function merge(primary: Invoice, others: Invoice[]): Invoice {
        if (!others.length) return primary;
        const sets = [primary, ...others];

        return {
            ...primary,
            billing: {
                adjustments: mergeAdjustments(sets),
                lineItems: mergeLineItems(sets),
                subtotals: mergeSubtotals(sets),
                totals: sumTotals(sets),
            },
            payments: sets.flatMap((invoice) => invoice.payments ?? []),
        };
    }

    /** Every set folded into the one `eventId` is priced by, whatever their number. */
    export function mergeAll(invoices: Invoice[], eventId: number | undefined): Invoice | undefined {
        if (invoices.length < 2) return invoices[0];

        const primary = invoices.find((invoice) => eventIdOf(invoice) === eventId) ?? invoices[0];
        return merge(
            primary,
            invoices.filter((invoice) => invoice !== primary),
        );
    }

    /**
     * An invoice list with every event's default set folded into the priced event's, which is what
     * keeps combining related events from reading as a row of identical Standard Invoice tabs. The
     * list is returned as it came when only one default set is in it.
     */
    export function foldDefaultSets(invoices: Invoice[], eventId: number | undefined): Invoice[] {
        const defaultSets = invoices.filter(isDefaultSet);
        if (defaultSets.length < 2) return invoices;

        const merged = mergeAll(defaultSets, eventId)!;
        const folded = new Set(defaultSets.filter((invoice) => invoice.id !== merged.id));

        return invoices
            .filter((invoice) => !folded.has(invoice))
            .map((invoice) => (isDefaultSet(invoice) ? merged : invoice));
    }
}
